import { useState } from 'react'
import { useMutation } from '@apollo/client'
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import { HourglassEmpty, Handshake, Person, Send } from '@mui/icons-material'
import { formatDistanceToNow, isPast } from 'date-fns'

import { useBranding } from '../../hooks/useBranding'
import { mutations } from '../../graphql'

export function SecuritiesInviteTile({ invite, onAcceptSuccess, onReinviteSuccess }) {
  const [accepting, setAccepting] = useState(false)
  const [reinviting, setReinviting] = useState(false)
  const { accentColor, accentTextColor } = useBranding()

  const isExpired = invite.expiresAt && isPast(new Date(invite.expiresAt))
  const canReinvite =
    isExpired && ['donor_advisor_invite', 'securities_advisor', 'admin_advisor_invite'].includes(invite.flowType)

  const [acceptConnection] = useMutation(mutations.onboarding.acceptAdvisorConnectionById, {
    onCompleted: () => {
      setAccepting(false)
      onAcceptSuccess?.('Connection accepted successfully.')
    },
    onError: (error) => {
      setAccepting(false)
      console.error('Failed to accept connection:', error)
    },
  })

  const [reinviteDonor] = useMutation(mutations.fundClaims.advisorReinviteDonor, {
    onCompleted: () => {
      setReinviting(false)
      onReinviteSuccess?.('A new invitation has been sent to the donor.')
    },
    onError: (error) => {
      setReinviting(false)
      console.error('Failed to reinvite donor:', error)
    },
  })

  const handleAccept = async () => {
    setAccepting(true)
    await acceptConnection({ variables: { flowId: invite.id } })
  }

  const handleReinvite = async () => {
    setReinviting(true)
    await reinviteDonor({ variables: { onboardingFlowId: invite.id } })
  }

  return (
    <Card
      sx={{
        height: '100%',
        width: 320,
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        opacity: 0.9,
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="div" gutterBottom>
          Pending Connection Request
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          A client has requested you as their advisor for a securities transfer.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 2 }}>
          <Tooltip
            title={
              isExpired
                ? 'This invitation has expired'
                : `Sent ${formatDistanceToNow(new Date(invite.createdAt), { addSuffix: true })}`
            }
          >
            <Chip
              label={isExpired ? 'Expired' : 'Pending Acceptance'}
              size="small"
              icon={<HourglassEmpty fontSize="small" />}
              sx={
                isExpired
                  ? { backgroundColor: 'error.main', color: 'error.contrastText' }
                  : { backgroundColor: accentColor, color: accentTextColor }
              }
            />
          </Tooltip>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <Person fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            <strong>From:</strong> {invite.donorName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Sent:</strong> {formatDistanceToNow(new Date(invite.createdAt), { addSuffix: true })}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        {canReinvite ? (
          <Button
            variant="contained"
            fullWidth
            onClick={handleReinvite}
            disabled={reinviting}
            startIcon={reinviting ? <CircularProgress size={16} color="inherit" /> : <Send />}
          >
            {reinviting ? 'Sending...' : 'Invite Donor Back'}
          </Button>
        ) : (
          <Button
            variant="contained"
            fullWidth
            onClick={handleAccept}
            disabled={accepting || isExpired}
            startIcon={accepting ? <CircularProgress size={16} color="inherit" /> : <Handshake />}
          >
            {accepting ? 'Accepting...' : 'Accept Connection'}
          </Button>
        )}
      </CardActions>
    </Card>
  )
}
